import { useRef } from 'react';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import areValuesEqual from '@/components/Questionnaire/form/helpers/areValuesEqual';
import getListOrTableField from '@/helpers/getListOrTableField';
import removeNullish from '@/helpers/removeNullish';

type Props = {
  aiAgent: { fieldOutputs: string[] };
  filter?: {
    fieldKeys: Set<string>;
    rowIndexes: Set<number>;
  }
};

const useOutputFieldKeys = ({ aiAgent, filter }: Props): string[] => {
  const {
    fieldsByKey,
    fieldsById,
  } = useQuestionnaireContext();
  const { formMethods } = useQuestionnaireFormContext();

  const storedFieldKeys = useRef<string[]>([]);

  // @todo - how to memoize this? Need to add items if a new row is added to the parent, so watch the form values, but all?
  const outputFieldKeys = aiAgent.fieldOutputs
    ? aiAgent.fieldOutputs
      .filter(fieldKey => !filter || filter.fieldKeys.has(fieldKey))
      .flatMap((fieldKey) => {
        const field = fieldsByKey[fieldKey];
        if (!field) {
          return [];
        }
        const listField = getListOrTableField({
          fieldId: field?.listFieldTemplateId,
          fieldsById,
        });
        if (listField) {
          const listFieldValues = formMethods.getValues()[listField.key] as unknown;
          if (!listFieldValues || !Array.isArray(listFieldValues) || listFieldValues?.length === 0) {
            return [];
          }
          return listFieldValues
            .map((__, index) => (!filter?.rowIndexes || filter.rowIndexes.has(index))
              ? `${listField.key}[${index}].${fieldKey}`
              : null,
            )
            .filter(removeNullish);
        }
        return fieldKey;
      })
    : [];

  if (!areValuesEqual(outputFieldKeys, storedFieldKeys.current)) {
    storedFieldKeys.current = outputFieldKeys;
  }

  return storedFieldKeys.current;
};

export default useOutputFieldKeys;
